import React, { useState, useEffect, useRef } from 'react';
import {
    Typography, Button, IconButton, MenuItem, Box,
    Alert, CircularProgress, TextField
} from '@mui/material';
import {
    Edit, Save, Cancel, Person, Description, Badge, VerifiedUser, Visibility, Close, Warning, CheckCircle
} from '@mui/icons-material';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import HeaderTopSection from '../../components/partials/HeaderTop';
import Banner from '../../components/partials/Banner';
import LoadingSpinner from '../../utils/LoadingSpinner';
import { useClientIdentification, PDF_ICON_URL, ACCEPTED_IMAGE_ATTR, ACCEPTED_DOC_ATTR, getFileType } from './hooks/useClientIdentification';

// La configuration globale du worker pdf.js est maintenant gérée dans src/index.js

const PAGE_GRADIENT = 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)';

const cardSx = {
    background: '#ffffff', borderRadius: '18px', padding: { xs: '20px', sm: '26px', md: '30px' },
    boxShadow: '0 4px 20px rgba(16,24,40,0.07)', border: '1px solid rgba(16,24,40,0.04)',
};

const sectionHeaderSx = {
    display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '22px',
    paddingBottom: '14px', borderBottom: '2px solid #eef1f6', flexWrap: 'wrap',
};

const sectionIconSx = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', width: 42, height: 42,
    borderRadius: '13px', flexShrink: 0, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff', boxShadow: '0 4px 12px rgba(102,126,234,0.35)',
};

const textFieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fbfcfe' },
    mt: 1, mb: 1,
};

const SectionCard = ({ icon, title, required, children }) => (
    <Box sx={cardSx}>
        <Box sx={sectionHeaderSx}>
            <Box sx={sectionIconSx}>{icon}</Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', flex: 1, minWidth: 120 }}>
                {title}
            </Typography>
            {required && <span style={{ background: '#fef2f2', color: '#e53935', fontWeight: 700, fontSize: 11, padding: '4px 13px', borderRadius: '20px' }}>Obligatoire</span>}
        </Box>
        {children}
    </Box>
);

const DisplayField = ({ label, value, icon, emptyText = 'Non spécifié' }) => (
    <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.3 }}>
            {icon} {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500, color: '#1a202c' }}>
            {value !== null && value !== undefined && value !== '' ? value : emptyText}
        </Typography>
    </Box>
);

const FileTypeIcon = ({ fileType, size = 60 }) => {
    if (fileType === 'pdf') return <img src={PDF_ICON_URL} alt="PDF" style={{ width: size, height: size }} />;
    return <Description sx={{ fontSize: size * 0.8, color: 'grey.400' }} />;
};

const FileDisplay = ({ url, label, onPreview }) => {
    const fileType = getFileType(url);
    if (!url) return (
        <Box sx={{ p: 2.5, border: '1px dashed', borderColor: 'grey.300', borderRadius: '12px', textAlign: 'center', background: '#fafbfc' }}>
            <Typography variant="body2" color="textSecondary">Aucun fichier</Typography>
        </Box>
    );
    return (
        <Box onClick={() => onPreview(url, fileType, label)} sx={{
            display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid', borderColor: 'grey.200',
            borderRadius: '12px', cursor: 'pointer', transition: '0.15s', background: '#fff',
            '&:hover': { boxShadow: '0 4px 14px rgba(16,24,40,0.1)', borderColor: 'primary.light', transform: 'translateY(-1px)' },
        }}>
            {fileType === 'image' ? (
                <img src={url} alt="Aperçu" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8 }} />
            ) : (
                <FileTypeIcon fileType={fileType} size={48} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>{label}</Typography>
                <Typography variant="caption" color="textSecondary">{fileType === 'image' ? 'Image' : 'Document PDF'}</Typography>
            </Box>
            <IconButton size="small" color="primary" sx={{ pointerEvents: 'none' }}><Visibility /></IconButton>
        </Box>
    );
};

const FileUploadZone = ({ fieldName, label, accept, required = false, styles, formData, handleFileUpload, handleRemoveFile, handleOpenPreview }) => {
    const file = formData[fieldName];
    const filePreview = formData[`${fieldName}Preview`];
    const fileUrl = formData[`${fieldName}Url`];
    const displayUrl = filePreview || fileUrl;
    const hasFile = !!displayUrl;

    const previewType = filePreview === 'pdf' ? 'pdf' : (filePreview?.startsWith('blob:') ? 'image' : getFileType(fileUrl));
    const previewSrc = (filePreview && filePreview !== 'pdf') ? filePreview : fileUrl;

    const zoneStyle = required ? (hasFile ? styles.uploadZoneValid : styles.uploadZone) : styles.uploadZone;

    return (
        <div style={zoneStyle}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                    {label} {required && <span style={{ color: styles.borderHex }}>*</span>}
                </Typography>
                {required && (hasFile
                        ? <Box style={styles.presentIndicator}><CheckCircle sx={{ fontSize: 15 }} /> Fourni</Box>
                        : <Box style={styles.missingIndicator}><Warning sx={{ fontSize: 15 }} /> Manquant</Box>
                )}
            </Box>

            {hasFile ? (
                <Box sx={{ textAlign: 'center' }}>
                    {previewType === 'image' && previewSrc ? (
                        <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                            <img src={previewSrc} alt="Aperçu" style={{ maxWidth: '100%', maxHeight: 190, borderRadius: 12, cursor: 'pointer' }} onClick={() => handleOpenPreview(previewSrc, 'image', label)} />
                        </Box>
                    ) : (
                        <Box sx={{ cursor: 'pointer', display: 'inline-block', mb: 1 }} onClick={() => handleOpenPreview(displayUrl, previewType, label)}>
                            <FileTypeIcon fileType={previewType} size={68} />
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#555' }}>{file?.name || 'Document PDF'}</Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1, flexWrap: 'wrap' }}>
                        <Button variant="outlined" component="label" size="small" sx={{ borderRadius: '999px' }}>
                            Remplacer
                            <input type="file" hidden accept={accept} onChange={e => handleFileUpload(e, fieldName)} />
                        </Button>
                        <Button variant="outlined" color="error" size="small" sx={{ borderRadius: '999px' }} onClick={() => handleRemoveFile(fieldName)}>
                            Supprimer
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Button fullWidth variant="contained" component="label" startIcon={<Description />} sx={{ mt: 0.5, borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                    Charger {label.toLowerCase()}
                    <input type="file" hidden accept={accept} onChange={e => handleFileUpload(e, fieldName)} />
                </Button>
            )}
        </div>
    );
};

// Lecteur PDF corrigé pour éviter le centrage vertical qui cache le début du document
const PdfReader = ({ url }) => {
    const [numPages, setNumPages] = useState(0);
    const [pdfError, setPdfError] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(true);
    const containerRef = useRef(null);
    const [pageWidth, setPageWidth] = useState(600);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                // 32px correspond à la marge intérieure (p: 2 = 16px * 2 côtés)
                setPageWidth(containerRef.current.clientWidth - 32);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    useEffect(() => {
        setPdfLoading(true);
        setPdfError(false);
        setNumPages(0);
    }, [url]);

    return (
        // Conteneur externe : gère uniquement le défilement (scroll)
        <Box ref={containerRef} sx={{ flex: 1, overflow: 'auto', background: '#f1f3f5', p: 2 }}>

            {pdfLoading && !pdfError && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>Chargement du document...</Typography>
                </Box>
            )}

            {pdfError && (
                <Typography color="error" sx={{ p: 4, textAlign: 'center' }}>Impossible de charger le PDF.</Typography>
            )}

            {/* Conteneur interne : gère uniquement le centrage horizontal */}
            {!pdfError && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Document
                        file={url}
                        onLoadSuccess={({ numPages }) => { setNumPages(numPages); setPdfLoading(false); }}
                        onLoadError={() => { setPdfError(true); setPdfLoading(false); }}
                        loading={null}
                    >
                        {Array.from({ length: numPages }, (_, i) => (
                            <Page
                                key={i}
                                pageNumber={i + 1}
                                width={pageWidth > 0 ? pageWidth : 600}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                            />
                        ))}
                    </Document>
                </Box>
            )}
        </Box>
    );
};

const DocumentPreviewModal = ({ docModal, handleCloseDocModal }) => {
    if (!docModal.open) return null;
    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.75)', zIndex: 1300, overflowY: 'auto', backdropFilter: 'blur(2px)' }} onClick={handleCloseDocModal}>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: 'white', borderRadius: '24px 24px 0 0', height: '95vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography variant="h6">{docModal.label || 'Aperçu Document'}</Typography>
                    <IconButton onClick={handleCloseDocModal}><Close /></IconButton>
                </div>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {docModal.type === 'pdf' && <PdfReader url={docModal.url} />}
                </Box>
                <div style={{ padding: '14px 20px', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
                    <Button variant="outlined" onClick={handleCloseDocModal} sx={{ borderRadius: 999, px: 3 }}>Fermer</Button>
                </div>
            </div>
        </div>
    );
};

const ClientIdentificationPage = () => {
    const {
        isEditMode, setIsEditMode, loading, saving, error, success,
        lightboxOpen, lightboxSlides, lightboxIndex, setLightboxIndex, handleCloseLightbox,
        docModal, handleCloseDocModal,
        formData, handleInputChange, handleFileUpload, handleRemoveFile,
        handleOpenPreview, handleSave, handleCancel, identStyles
    } = useClientIdentification();

    if (loading) {
        return (
            <>
                <HeaderTopSection />
                <Banner title="Mon Profil Client" />
                <div style={{ background: PAGE_GRADIENT, minHeight: '100vh' }}>
                    <LoadingSpinner isVisible={true} loadingText="Chargement de votre profil..." />
                </div>
            </>
        );
    }

    return (
        <>
            <HeaderTopSection />
            <Banner title="Mon Profil Client" />
            <div className="profile-main-content" style={{ background: PAGE_GRADIENT, minHeight: '100vh', padding: '32px 16px' }}>
                <div className="profile-card" style={{ maxWidth: 1000, margin: '0 auto' }}>
                    <Box sx={{ background: '#ffffff', borderRadius: '22px', boxShadow: '0 10px 40px rgba(16,24,40,0.12)', p: { xs: 2.5, sm: 4 } }}>

                        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}
                        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>{success}</Alert>}

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #1565c0 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                                Identification &amp; Documents
                            </Typography>
                            {!isEditMode ? (
                                <Button variant="contained" startIcon={<Edit />} onClick={() => setIsEditMode(true)} sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 600, px: 3 }}>
                                    Modifier
                                </Button>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancel} disabled={saving} sx={{ borderRadius: '999px', textTransform: 'none' }}>
                                        Annuler
                                    </Button>
                                    <Button variant="contained" startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />} onClick={handleSave} disabled={saving} sx={{ borderRadius: '999px', textTransform: 'none', background: 'linear-gradient(135deg, #1565c0 0%, #764ba2 100%)' }}>
                                        {saving ? 'Sauvegarde...' : 'Enregistrer'}
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                            {!isEditMode && (
                                <SectionCard icon={<Person />} title="Informations Personnelles">
                                    <div className="row g-3">
                                        <div className="col-12 col-sm-6"><DisplayField label="Nom complet" value={`${formData.firstName} ${formData.lastName}`} icon={<Person sx={{ fontSize: 16 }} />} /></div>
                                        <div className="col-12 col-sm-6"><DisplayField label="Email" value={formData.email} icon={<Person sx={{ fontSize: 16 }} />} /></div>
                                        <div className="col-12 col-sm-6"><DisplayField label="Téléphone" value={formData.phoneNumber} icon={<Person sx={{ fontSize: 16 }} />} /></div>
                                        <div className="col-12 col-sm-6"><DisplayField label="Ville" value={formData.city} icon={<Person sx={{ fontSize: 16 }} />} /></div>
                                        <div className="col-12 col-sm-6"><DisplayField label="Quartier" value={formData.neighborhood} icon={<Person sx={{ fontSize: 16 }} />} /></div>
                                        <div className="col-12 col-sm-6"><DisplayField label="Pays" value={formData.country} icon={<Person sx={{ fontSize: 16 }} />} /></div>
                                    </div>
                                </SectionCard>
                            )}

                            <Box style={identStyles.sectionWrapper}>
                                <Box style={identStyles.sectionTitle}>
                                    <Badge sx={{ color: identStyles.iconColor, fontSize: 26 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: identStyles.titleColor }}>
                                        Pièces d'Identité
                                    </Typography>
                                </Box>

                                <Box style={identStyles.infoAlert}>
                                    <VerifiedUser sx={{ color: '#f59e0b', fontSize: 20, flexShrink: 0, mt: '1px' }} />
                                    <span>
                                        Veuillez fournir une photo de profil et un document d'identité valide (Recto uniquement).
                                        Le type de document est modifiable.
                                    </span>
                                </Box>

                                {/* Utilisation d'une classe dynamique pour l'affichage en pleine largeur en mode édition */}
                                <div className="row g-3">
                                    <div className={`col-12 ${!isEditMode ? 'col-sm-6' : ''}`}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Type de document"
                                            name="identityDocumentType"
                                            value={formData.identityDocumentType}
                                            onChange={handleInputChange}
                                            disabled={!isEditMode}
                                            variant="outlined"
                                            sx={textFieldSx}
                                        >
                                            <MenuItem value="CNI">Carte nationale d'identité</MenuItem>
                                            <MenuItem value="PASSPORT">Passeport</MenuItem>
                                            <MenuItem value="DRIVING_LICENSE">Permis de conduire</MenuItem>
                                            <MenuItem value="RESIDENCE_PERMIT">Titre de séjour</MenuItem>
                                        </TextField>
                                    </div>
                                    {!isEditMode && (
                                        <div className="col-12 col-sm-6">
                                            <DisplayField label="Total de contrats" value={formData.totalNumOfContracts ?? 0} icon={<Description sx={{ fontSize: 16 }} />} />
                                        </div>
                                    )}
                                </div>

                                <div className="row g-3 mt-1">
                                    <div className="col-12 col-sm-6">
                                        {isEditMode ? (
                                            <FileUploadZone
                                                fieldName="profilePhoto"
                                                label="Photo de profil"
                                                accept={ACCEPTED_IMAGE_ATTR}
                                                required
                                                styles={identStyles}
                                                formData={formData}
                                                handleFileUpload={handleFileUpload}
                                                handleRemoveFile={handleRemoveFile}
                                                handleOpenPreview={handleOpenPreview}
                                            />
                                        ) : (
                                            <>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>Photo de profil</Typography>
                                                <FileDisplay url={formData.profilePhotoUrl} label="Photo de profil" onPreview={handleOpenPreview} />
                                            </>
                                        )}
                                    </div>
                                    <div className="col-12 col-sm-6">
                                        {isEditMode ? (
                                            <FileUploadZone
                                                fieldName="identityDocument"
                                                label={`Document (${formData.identityDocumentType})`}
                                                accept={ACCEPTED_DOC_ATTR}
                                                required
                                                styles={identStyles}
                                                formData={formData}
                                                handleFileUpload={handleFileUpload}
                                                handleRemoveFile={handleRemoveFile}
                                                handleOpenPreview={handleOpenPreview}
                                            />
                                        ) : (
                                            <>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>Document d'identité</Typography>
                                                <FileDisplay url={formData.identityDocumentUrl} label="Document d'identité" onPreview={handleOpenPreview} />
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Box>

                        </Box>
                    </Box>
                </div>

                <Lightbox
                    open={lightboxOpen}
                    close={handleCloseLightbox}
                    slides={lightboxSlides}
                    index={lightboxIndex}
                    on={{ view: ({ index }) => setLightboxIndex(index) }}
                    plugins={[Zoom]}
                    zoom={{ maxZoomPixelRatio: 3 }}
                    controller={{ closeOnBackdropClick: true }}
                />

                <DocumentPreviewModal docModal={docModal} handleCloseDocModal={handleCloseDocModal} />
            </div>

            {saving && <LoadingSpinner isVisible={true} loadingText="Sauvegarde en cours..." />}
        </>
    );
};

export default ClientIdentificationPage;